/**
 * Daily logs — chunk 2, step 1.
 *
 * `features/requirements/dailyLogs.ts` composed 29 reads and 11 writes across
 * seven tables. It is not a screen: twelve files import it, spanning four
 * chunks (site-ops ×8, the deal profile, mobile, the requirements panel, a
 * dashboard hook). Its exported signatures are deliberately unchanged — only
 * what happens inside them moved here.
 *
 * Contract mirror: labourcore-api/src/types/dailyLogs.types.ts.
 *
 * ── What starts working ───────────────────────────────────────────────────
 *
 * 1. THE AUDIT TRAIL. `daily_log_versions` has UNIQUE (log_id, version), and
 *    confirm/flag/lock all wrote `row.version` — still 1 after a submit. Every
 *    one collided with the submit row, and `writeVersion` swallowed the error
 *    as "a recoverable audit deficiency". It never recovered. The trail has
 *    only ever contained `submit`. Version numbers are now a sequence per log.
 *
 * 2. THE AUDIT EXPORT. `fetchPlacementAuditTrail` read `daily_log_versions`
 *    with no filter at all and grouped by log_id in JS, so the rows for the
 *    requested placement could fall outside the row limit entirely. Scoped in
 *    SQL now, so completeness does not depend on a LIMIT.
 *
 * 3. TENANT SCOPE. Four reads had none and relied on RLS. RLS is disabled on
 *    all seven tables — verified, not assumed.
 *
 * 4. STATE TRANSITIONS ARE ATOMIC. confirm/lock/amend read the row, checked its
 *    status in the browser, then updated — two round trips two users could
 *    interleave. The guard is in the UPDATE now; losing the race gives a 409.
 */

import { apiGet, apiPost, apiDelete } from "./client";

export type Row = Record<string, unknown>;

export interface LogTotals {
  payable_hours: number;
  locked_hours: number;
  confirmed_log_count: number;
  locked_log_count: number;
}

export interface PlacementLogs {
  logs: Row[];
  versions: Row[];
  attachments: Row[];
  totals: LogTotals;
  milestones: Row[];
  /**
   * Chunk 2, item 2. Both had a dropped embed, so no dispute ever showed who
   * filed it and no rating ever showed who left it. Carried in this bundle so
   * the log panel makes one request instead of three.
   */
  disputes: Row[];
  ratings: Row[];
}

/** Logs, versions, attachments, totals, milestones, disputes and ratings — one call. */
export function fetchPlacementLogs(placementId: string): Promise<PlacementLogs> {
  return apiGet<PlacementLogs>(`/api/placements/${placementId}/daily-logs`);
}

/** Counterparty / operative / location names for the exception queue. */
export function fetchExceptionPlacements(placementIds: string[]): Promise<Row[]> {
  return apiPost<Row[]>("/api/site-ops/exception-placements", { placement_ids: placementIds });
}

export function fetchMissingLogs(logDate: string): Promise<Row[]> {
  return apiGet<Row[]>("/api/site-ops/missing-logs", { date: logDate });
}

export function fetchPaymentReadiness(): Promise<Row[]> {
  return apiGet<Row[]>("/api/site-ops/payment-readiness");
}

/* ── Log lifecycle ──────────────────────────────────────────────────────── */

export interface SubmitLogBody {
  placement_id: string;
  operative_id?: string | null;
  log_date: string;
  planned_hours: number;
  planned_headcount: number;
  actual_hours: number;
  actual_headcount: number;
  variance_reason?: string | null;
  notes?: string | null;
  /** From the caller's existing compliance check — not recomputed server-side. */
  compliance_lapsed?: boolean;
  compliance_lapse_summary?: string | null;
}

export function submitLogApi(body: SubmitLogBody): Promise<Row> {
  return apiPost<Row>("/api/daily-logs", body);
}
export function confirmLogApi(logId: string): Promise<Row> {
  return apiPost<Row>(`/api/daily-logs/${logId}/confirm`);
}
export function flagLogApi(logId: string, reason: string): Promise<Row> {
  return apiPost<Row>(`/api/daily-logs/${logId}/flag`, { reason });
}
export function lockLogApi(logId: string): Promise<Row> {
  return apiPost<Row>(`/api/daily-logs/${logId}/lock`);
}
export function amendLogApi(
  logId: string,
  body: { actual_hours: number; actual_headcount: number; variance_reason?: string | null; notes?: string | null },
): Promise<Row> {
  return apiPost<Row>(`/api/daily-logs/${logId}/amend`, body);
}

/* ── Attachments ────────────────────────────────────────────────────────── */

export function addAttachmentApi(logId: string, row: Row): Promise<Row> {
  return apiPost<Row>(`/api/daily-logs/${logId}/attachments`, row);
}
export function deleteAttachmentApi(attachmentId: string): Promise<{ ok: true }> {
  return apiDelete(`/api/attachments/${attachmentId}`);
}

/* ── Saved site-ops views ───────────────────────────────────────────────── */

export function fetchSavedViews(tab: string): Promise<Row[]> {
  return apiGet<Row[]>("/api/site-ops/saved-views", { tab });
}
export function createSavedViewApi(tab: string, name: string, params: unknown): Promise<Row> {
  return apiPost<Row>("/api/site-ops/saved-views", { tab, name, params });
}
export function deleteSavedViewApi(viewId: string): Promise<{ ok: true }> {
  return apiDelete(`/api/site-ops/saved-views/${viewId}`);
}

export interface LogDetail {
  log: Row;
  versions: Row[];
  attachments: Row[];
}

/**
 * One log with its version trail and attachments.
 *
 * Exists because three client helpers are log-scoped, not placement-scoped, and
 * their signatures must not change — twelve files import that module. Shaping
 * the endpoint to the question the caller actually asks is cheaper and honester
 * than making the caller look up a placement id first.
 */
export function fetchLogDetail(logId: string): Promise<LogDetail> {
  return apiGet<LogDetail>(`/api/daily-logs/${logId}`);
}
